import React from 'react';

export const ProfilePane = ({
  activeIdentity,
  displayGaiaID,
  profileAvatar,
  setProfileAvatar,
  profileDisplayName,
  setProfileDisplayName,
  profileBio,
  setProfileBio,
  saveProfile,
  handleAvatarFileChange,
  currentPasswordInput,
  setCurrentPasswordInput,
  newPasswordInput,
  setNewPasswordInput,
  confirmPasswordInput,
  setConfirmPasswordInput,
  passwordChangeError,
  handleChangePassword,
  areKeysUnlocked,
  profilePasswordInput,
  setProfilePasswordInput,
  handleUnlockProfileKeys,
  profileUnlockError,
  derivedKeys,
  mnemonic,
  setAreKeysUnlocked,
  setCurrentMenu,
  t
}) => {
  return (
    <div className="profile-container">
      <button type="button" className="mobile-back-btn" onClick={() => setCurrentMenu('inbox')}>← {t('wizard_back') || 'Zurück'}</button>
      <h2>{t('mein_profil') || 'Dein dezentrales Benutzerprofil'}</h2>
      
      <div className="profile-grid-layout">
        {/* LEFT COLUMN: Profile info & Edit Form */}
        <div className="profile-column-left">
          <div className="profile-card">
            <div className="profile-avatar-big">
              {profileAvatar.startsWith('data:image/') ? (
                <img src={profileAvatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                profileAvatar
              )}
            </div>
            <div>
              <h3>{profileDisplayName || (t('no_specification') || 'Kein Anzeigename')}</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{activeIdentity ? displayGaiaID(activeIdentity.GaiaID) : ''}</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '6px' }}>{t('biography') || 'Biografie'}: {profileBio || (t('no_specification') || 'Keine Angabe')}</p>
            </div>
          </div>

          <div className="profile-section-card">
            <div className="profile-section-title">{t('edit_profile') || 'Profil bearbeiten'}</div>
            <form onSubmit={saveProfile}>
              <div className="form-group">
                <label>{t('display_name') || 'Anzeigename'}</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={profileDisplayName} 
                  onChange={e => setProfileDisplayName(e.target.value)} 
                  required 
                />
              </div>

              <div className="form-group">
                <label>{t('bio_status') || 'Biografie / Status'}</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={profileBio} 
                  onChange={e => setProfileBio(e.target.value)} 
                />
              </div>

              <div className="form-group">
                <label>{t('choose_avatar') || 'Avatar wählen'}</label>
                
                <div className="custom-avatar-upload-row">
                  <div className="avatar-upload-preview">
                    {profileAvatar.startsWith('data:image/') ? (
                      <img src={profileAvatar} alt="Avatar" />
                    ) : (
                      <span>{profileAvatar}</span>
                    )}
                  </div>
                  <label className="avatar-upload-btn">
                    {t('upload_avatar_btn') || 'Bild hochladen (Sanitized)'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleAvatarFileChange} 
                      style={{ display: 'none' }} 
                    />
                  </label>
                </div>

                <div className="avatar-grid">
                  {['🤖', '👽', '🚀', '🛡️', '🌐', '🌌', '🧬', '💻', '🧠', '⚡', '✨', '🔥'].map(av => (
                    <div 
                      key={av} 
                      className={`avatar-item ${profileAvatar === av ? 'active' : ''}`}
                      onClick={() => setProfileAvatar(av)}
                    >
                      {av}
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 30px' }}>
                {t('save_profile') || 'Profil speichern'}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT COLUMN: Password Change & Cryptographic Keys */}
        <div className="profile-column-right">
          <div className="profile-section-card">
            <div className="profile-section-title">
              {t('change_password_title') || 'Passwort ändern'}
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.5' }}>
              {t('change_password_desc') || 'Ändert dein serverseitiges Login-Passwort. Deine lokale Recovery Phrase bleibt separat durch dein Vault-/Unlock-Passwort geschützt.'}
            </p>
            
            <form onSubmit={handleChangePassword}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>{t('current_password') || 'Aktuelles Passwort'}</label>
                <input
                  type="password"
                  className="input-field"
                  value={currentPasswordInput}
                  onChange={e => setCurrentPasswordInput(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>{t('new_password') || 'Neues Passwort'}</label>
                <input
                  type="password"
                  className="input-field"
                  value={newPasswordInput}
                  onChange={e => setNewPasswordInput(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>{t('confirm_new_password') || 'Neues Passwort confirmar'}</label>
                <input
                  type="password"
                  className="input-field"
                  value={confirmPasswordInput}
                  onChange={e => setConfirmPasswordInput(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              
              {passwordChangeError && (
                <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: '15px' }}>
                  {passwordChangeError}
                </p>
              )}
              
              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 30px' }}>
                {t('update_password') || 'Passwort aktualisieren'}
              </button>
            </form>
          </div>

          <div className="profile-section-card">
            <div className="profile-section-title">
              {t('kryptographische_schluessel') || 'Kryptographische Schlüssel'}
            </div>
            
            {!areKeysUnlocked ? (
              <div className="crypto-unlock-prompt" style={{ marginTop: '10px' }}>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  {t('profile_unlock_desc') || 'Aus Sicherheitsgründen müssen Sie Ihr Passwort erneut eingeben, um Ihre privaten kryptographischen Schlüssel anzuzeigen.'}
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="password"
                    className="input-field"
                    placeholder={t('vault_pwd_placeholder') || 'Passwort eingeben...'}
                    value={profilePasswordInput}
                    onChange={e => setProfilePasswordInput(e.target.value)}
                    style={{ maxWidth: '280px' }}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ width: 'auto', padding: '0 20px' }}
                    onClick={handleUnlockProfileKeys}
                  >
                    🔑 {t('unlock_btn') || 'Freischalten'}
                  </button>
                </div>
                {profileUnlockError && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '8px' }}>{profileUnlockError}</p>}
              </div>
            ) : (
              <>
                <div className="crypto-grid">
                  <div className="crypto-card">
                    <div className="crypto-title">Ed25519 Identität (Sign Public)</div>
                    <div className="crypto-value">{derivedKeys?.sign.public}</div>
                  </div>
                  <div className="crypto-card">
                    <div className="crypto-title">Ed25519 Identität (Sign Private)</div>
                    <div className="crypto-value">{derivedKeys?.sign.private}</div>
                  </div>
                  <div className="crypto-card">
                    <div className="crypto-title">X25519 Box (Public)</div>
                    <div className="crypto-value">{derivedKeys?.box.public}</div>
                  </div>
                  <div className="crypto-card">
                    <div className="crypto-title">X25519 Box (Private)</div>
                    <div className="crypto-value">{derivedKeys?.box.private}</div>
                  </div>
                  <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                    <div className="crypto-title">ML-KEM-1024 Post-Quantum PKE (Public)</div>
                    <div className="crypto-value">{derivedKeys?.pke.public}</div>
                  </div>
                  <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                    <div className="crypto-title">ML-KEM-1024 Post-Quantum PKE (Private/Secret)</div>
                    <div className="crypto-value" style={{ maxHeight: '100px', overflowY: 'auto' }}>{derivedKeys?.pke.private}</div>
                  </div>
                  <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                    <div className="crypto-title">Mnemonic Recovery Seed Phrase (12 Wörter)</div>
                    <div className="crypto-value" style={{ color: 'var(--warning)', fontWeight: 'bold' }}>{mnemonic}</div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="btn-secondary" 
                  style={{ width: '100%', marginTop: '15px' }}
                  onClick={() => setAreKeysUnlocked(false)}
                >
                  🔑 {t('schluessel_entsperrt') || 'Schlüssel wieder sperren'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePane;
